import { useState, useEffect, useRef, useContext, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import AutoSizer from "react-virtualized-auto-sizer";
import useExport from "@apiHooks/useExport";
import { getThousandAlias, getFormattedThousandWithDecimal } from "@core/helpers";
import { IconDownload } from "@icons";
import { currentCountryWithPermissionsSelector } from "@selectors/countries.selector";
import { useDefaultFilterData, useFilters, useMappedFiltersToApi } from "@vms/SelectFilters/SelectFiltersContext";
import BarChart from "@components/BarChart/BarChart";
import Loader from "@components/Loader/Loader";
import { GlobalContext } from "@components/App/App.context";
import Chart from "@components/Chart/Chart";
import { UseByoChartDataResult } from "@pages/Byo/useByoChartData";
import { useByoDataContext } from "../../ByoContext";
import "./ChartPanel.scss";

interface ChartDimensions {
    chartSvgWidth: number;
    chartSvgHeight: number;
    chartSvgCoefficient: number;
}

interface ChartPanelProps extends UseByoChartDataResult {
    isLoading: boolean;
}

const CHART_EXPORT_CONFIG = {
    exportEndpoint: 'export BYO chart',
    downloadEndpoint: 'export BYO chart',
};

const DEFAULT_CHART_WIDTH = 1350;
const CURRENCY_TAB_ID = 3;

const parsePixelValue = (cssValue: string): number => 
    Number.parseInt(cssValue.replace("px", ""));

const ChartPanel = ({
    chartData,
    barChartData,
    isLoading,
}: ChartPanelProps) => {
    const { data: words, activeAnalyticsType } = useByoDataContext();
    const { getRem } = useContext(GlobalContext);

    const chartContainerEl = useRef<HTMLDivElement>(null);
    const permissions = useSelector(currentCountryWithPermissionsSelector);
    const [dimensions, setDimensions] = useState<ChartDimensions>({
        chartSvgWidth: 0,
        chartSvgHeight: 0,
        chartSvgCoefficient: 0,
    });
    
    const exportChartReport = useExport(CHART_EXPORT_CONFIG);
    const { countryCode, currentCurrency } = useFilters();
    const defaultFilterData = useDefaultFilterData();
    const mapFiltersToApi = useMappedFiltersToApi();

    const keywords = useMemo(() => words.map(d => d.keyword), [words]);

    const exportData = useMemo(() => ({
        customerFilter: {
            mainFilter: { filters: mapFiltersToApi, words: keywords },
        },
        fullFilter: { ...defaultFilterData, countryCode },
    }), [mapFiltersToApi, keywords, defaultFilterData, countryCode]);
    
    const updateDimensions = useCallback(() => {
        if (!chartContainerEl.current) return;
        
        const defaultChartWidth = getRem(DEFAULT_CHART_WIDTH);
        const chartStyles = getComputedStyle(chartContainerEl.current);
        const chartSvgWidth = parsePixelValue(chartStyles.width);
        const chartSvgHeight = parsePixelValue(chartStyles.height);
        const chartSvgCoefficient = chartSvgWidth / defaultChartWidth;

        if (dimensions.chartSvgWidth !== chartSvgWidth || dimensions.chartSvgHeight !== chartSvgHeight) {
            setDimensions({
                chartSvgWidth,
                chartSvgHeight,
                chartSvgCoefficient,
            });
        }
    }, [getRem, dimensions]);

    const downloadChartReport = useCallback(
        () => exportChartReport(exportData),
        [exportData, exportChartReport]
    );

    useEffect(() => {
        updateDimensions();
        window.addEventListener("resize", updateDimensions);

        return () => {
            window.removeEventListener("resize", updateDimensions);
        };
    }, [updateDimensions]);

    const formatValue = useCallback((formatter: (n: number) => string) => (n: number): string => {
        if (activeAnalyticsType === CURRENCY_TAB_ID) {
            return `${currentCurrency}${formatter(n)}`;
        }
        
        return `${n % 1 === 0 ? n : n.toFixed(1)}%`;
    }, [activeAnalyticsType, currentCurrency]);

    const printValue = useMemo(() => formatValue(getFormattedThousandWithDecimal), [formatValue]);
    const printLabel = useMemo(() => formatValue(getThousandAlias), [formatValue]);

    return (
        <div ref={chartContainerEl} className="ChartPanel">
            {permissions?.hasChart && dimensions.chartSvgWidth > 0 && chartData.columns.length > 0 && (
                <AutoSizer>
                    {({ width, height }) => (
                        <>
                            <Chart
                                charts={chartData.charts}
                                columns={chartData.columns}
                                width={width}
                                height={height}
                                printValue={printValue}
                                printLabel={printLabel}
                                dashStartPointColumnIndex={chartData.dashStartPointColumnIndex}
                                dashStartPointSubColumnIndex={chartData.dashStartPointSubColumnIndex}
                            />
                            <button
                                className="ChartPanel__button"
                                onClick={downloadChartReport}
                                title="Download chart"
                            >
                                <IconDownload/>
                            </button>
                        </>
                    )}
                </AutoSizer>
            )}
            {!permissions?.hasChart && (
                <BarChart
                    data={barChartData}
                    height={dimensions.chartSvgHeight}
                    activeTabId={activeAnalyticsType}
                />
            )}
            <Loader isShown={isLoading}/>
        </div>
    );
};

export default ChartPanel;
